import { Expose, Transform, plainToClassFromExist } from 'class-transformer';
import { Subject, Subscription, Observable, fromEvent } from 'rxjs';
import {
  map,
  tap,
  filter,
  pairwise,
  takeUntil,
  exhaustMap,
  catchError,
  throttleTime,
} from 'rxjs/operators';

import { shouldTriggerEvents, isScrollingDown } from './scroll.helpers';
import { ISnScrollPosition, ISnScrollConfig } from './scroll.interfaces';

export class SnScrollReference {
  @Expose() @Transform((value) => value || 20) public threshold = 20;
  @Expose() @Transform((value) => value || 15) public thresholdUp = 15;

  private _container: HTMLElement | Document;

  private readonly _scrolled$ = new Subject<ISnScrollPosition>();
  private readonly _scrolledUp$ = new Subject<void>();
  private readonly _scrolledDown$ = new Subject<void>();

  private readonly _destroy$ = new Subject<void>();

  private _containerScrollSubscription: Subscription;

  constructor(container?: HTMLElement | Document, config?: ISnScrollConfig) {
    this.container = container;
    plainToClassFromExist(this, config, { strategy: 'excludeAll' });

    this._initScrollEvents();
  }

  get scrolledUp$(): Observable<void> {
    return this._scrolledUp$.asObservable();
  }

  get scrolledDown$(): Observable<void> {
    return this._scrolledDown$.asObservable();
  }

  set container(container: HTMLElement | Document) {
    if (this._container !== container) {
      this._container = container;

      this._destroyContainerScrollSubscription();
      this._initContainerScrollSubscription(container);
    }
  }

  public loadUp<T>(fetchFn: () => Observable<T>): Observable<T> {
    return this._scrolledUp$.pipe(
      exhaustMap(fetchFn),
      catchError((error, source$) => source$),
    );
  }

  public loadDown<T>(fetchFn: () => Observable<T>): Observable<T> {
    return this._scrolledDown$.pipe(
      exhaustMap(fetchFn),
      catchError((error, source$) => source$),
    );
  }

  public destroy(): void {
    this._scrolled$.complete();
    this._scrolledUp$.complete();
    this._scrolledDown$.complete();

    this._destroy$.next();
    this._destroy$.complete();
  }

  private _initScrollEvents(): void {
    this._scrolled$
      .pipe(
        throttleTime(10),
        pairwise(),
        filter(([prevPos, currenPos]) => {
          return shouldTriggerEvents(prevPos, currenPos, this.threshold, this.thresholdUp);
        }),
        tap(([prevPos, currenPos]) => {
          if (isScrollingDown(prevPos, currenPos)) {
            this._scrolledDown$.next();
          } else {
            this._scrolledUp$.next();
          }
        }),
      )
      .subscribe();
  }

  private _initContainerScrollSubscription(container: HTMLElement | Document): void {
    this._containerScrollSubscription = fromEvent(container, 'scroll')
      .pipe(
        map(() => {
          const element = container instanceof Document ? document.documentElement : container;

          return {
            scrollTop: element.scrollTop,
            scrollHeight: element.scrollHeight,
            clientHeight: element.clientHeight,
          };
        }),
        takeUntil(this._destroy$),
      )
      .subscribe((position) => this._scrolled$.next(position));
  }

  private _destroyContainerScrollSubscription(): void {
    if (this._containerScrollSubscription) {
      this._containerScrollSubscription.unsubscribe();
      this._containerScrollSubscription = null;
    }
  }
}
